import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "react-query";

import { useDrawers } from "../contexts/Drawers";
import { useTemplateContext as useTemplate } from "../components/providers/Template";
import DrawerHeader from "../components/DrawerHeader";
import { Loader } from "../components/fragments/Loader/loader";
import Text from "../components/elementsThemed/Text/text";
import View from "../components/elementsThemed/View/View";

import { GET_GIFTCARD_CONFIGS } from "../utils/api";

interface AccountDrawerProps {
  onLogoutClick?: (e?: React.MouseEvent) => void;
}

export const giftCardLabels = {
  purchase: "Buy a Gift Card",
  checkBalance: "Check Balance of Gift Card",
  reload: "Reload Gift card",
};

type ClickFnType = (e?: React.MouseEvent) => void;

function NavigationItems({
  menu,
}: {
  menu: Record<string, string | ClickFnType>;
}) {
  const {
    theme: {
      colors,
      styles,
      elements: { buttons, labels },
    },
  } = useTemplate();

  const buttonTextType =
    buttons?.[styles?.normal?.main?.buttons?.dropdown]?.stateTextStyles
      ?.unselected;

  const vectorColor =
    colors?.[labels?.[buttonTextType]?.textColor] || "#00523B";

  return (
    <>
      {Object.keys(menu).map((item) => (
        <li key={item} style={{ margin: "0", padding: "1.5rem" }}>
          <a
            role="button"
            tabIndex={0}
            style={{ display: "flex" }}
            {...(typeof menu[item] === "string"
              ? {
                  href: menu[item] as string,
                }
              : {
                  onClick: menu[item] as ClickFnType,
                })}
          >
            <div style={{ flexGrow: "1" }}>
              <Text type={buttonTextType}>{item}</Text>
            </div>
            <svg
              width="12"
              height="18"
              viewBox="0 0 12 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.84638 16.5L10 9.09325L1.84638 1.5"
                stroke={vectorColor}
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </a>
        </li>
      ))}
    </>
  );
}

function Navigation(props) {
  return (
    <motion.ul
      transition={{ ease: "easeInOut", duration: 0.3 }}
      style={{
        display: "flex",
        flexGrow: "1",
        flexDirection: "column",
        listStyle: "none",
        margin: "0",
        padding: "0",
      }}
      {...props}
    />
  );
}

function GiftCards() {
  const { navigate } = useDrawers();
  const { data, isLoading } = useQuery("giftcardConfig", GET_GIFTCARD_CONFIGS, {
    onSuccess: ({ data: queryData }) => {
      // Fallback when no giftcard config is found
      if (!queryData?.length) {
        return navigate("/profile");
      }

      return null;
    },
  });

  const isOffSite =
    data?.data?.filter(({ type }) => type === "offSite").length > 0;

  const menus = data?.data?.map(({ config }) =>
    Object.keys(config).reduce(
      (accu, type) =>
        config[type]?.enabled && config[type]?.url && giftCardLabels[type]
          ? {
              ...accu,
              [giftCardLabels[type]]: config[type]?.url,
            }
          : accu,
      {},
    ),
  );

  return (
    <Navigation initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}>
      {isLoading ? (
        <Loader />
      ) : (
        menus?.map((menuData) => <NavigationItems menu={menuData} />)
      )}
      {isOffSite && (
        <li>
          <p
            style={{
              fontSize: "0.875rem",
              lineHeight: "1.25rem",
              margin: "1rem auto",
              maxWidth: "20rem",
            }}
          >
            * You will be taken to an external site to buy, reload or check the
            balance of a gift card.
          </p>
        </li>
      )}
    </Navigation>
  );
}

function MainMenu({ menu, onLogoutClick }) {
  const {
    theme: {
      styles,
      elements: { buttons },
    },
  } = useTemplate();

  const buttonTextType =
    buttons?.[styles?.normal?.main?.buttons?.dropdown]?.stateTextStyles
      ?.unselected;

  return (
    <Navigation
      initial={{ x: "-100%" }}
      animate={{ x: 0 }}
      exit={{ x: "-100%" }}
    >
      <NavigationItems {...{ menu }} />
      <li style={{ margin: "0", padding: "1.5rem" }}>
        <Text
          type={buttonTextType}
          onClick={onLogoutClick}
          style={{ cursor: "pointer" }}
        >
          <strong>Logout</strong>
        </Text>
      </li>
    </Navigation>
  );
}

export default function AccountDrawer({ onLogoutClick }: AccountDrawerProps) {
  const { navigate, closeDrawer, titleProps } = useDrawers();
  const {
    theme: {
      elements: { cells },
    },
  } = useTemplate();

  if (!cells?.accountDrawer) {
    throw new Error("Missing accountDrawer Cell in Client Theme.");
  }

  const [drawerRoute, setDrawerRoute] = React.useState("/");

  const menu = {
    Profile: () => navigate("/profile"),
    "Gift Cards": () => setDrawerRoute("/gift-cards"),
    "Customer Support": () => null,
  };

  const handleLogoutClick = () => {
    closeDrawer();
    onLogoutClick();
  };

  return (
    <View type={cells.accountDrawer.views.background}>
      <DrawerHeader
        title="Account"
        {...titleProps}
        hideBackButton={drawerRoute === "/"}
        backButtonProps={{
          onClick: () => setDrawerRoute("/"),
        }}
      />
      <AnimatePresence>
        {drawerRoute === "/" && (
          <MainMenu onLogoutClick={handleLogoutClick} {...{ menu }} />
        )}
        {drawerRoute === "/gift-cards" && <GiftCards />}
      </AnimatePresence>
    </View>
  );
}

AccountDrawer.defaultProps = {
  onLogoutClick: () => null,
};
